import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Bold, Italic, Heading1, Heading2, List, Columns2, Columns3, Pilcrow, Eraser } from 'lucide-react';
import { cn } from '../lib/utils';

export function RichTextarea({ value, onChange, placeholder, className, rows = 4, linkText, linkHref }) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Convert value to HTML for display
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const applyHeading = (level) => {
    // Use formatBlock to properly replace/toggle headings (not nest them)
    applyFormat('formatBlock', `<${level}>`);
  };

  const applyParagraph = () => {
    applyFormat('formatBlock', 'p');
  };

  const clearFormatting = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    if (selectedText) {
      // Remove all formatting, insert as plain text
      range.deleteContents();
      const textNode = document.createTextNode(selectedText);
      range.insertNode(textNode);
      
      // If we're in a heading or formatted block, convert to paragraph
      applyFormat('formatBlock', 'p');
      applyFormat('removeFormat');
      
      editorRef.current?.focus();
      handleInput();
    }
  };

  const insertColumns = (count) => {
    const columns = Array(count).fill(0).map(() => 
      '<td style="width: ' + (Math.floor(94/count)) + '%; padding: 0; vertical-align: top;"><p>Column content...</p></td>'
    ).join('<td style="width: ' + (Math.floor(6/count)) + '%;"></td>');
    
    const html = `
      <table class="column-table" style="width: 100%; margin: 12px 0 12px 0; border-collapse: collapse;">
        <tr>
          ${columns}
        </tr>
      </table>
      <p><br></p>
    `;
    document.execCommand('insertHTML', false, html);
    editorRef.current?.focus();
    handleInput();
  };

  // Allow formatted paste but handle heading context
  const handlePaste = (e) => {
    e.preventDefault();
    
    // Get clipboard data
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    // Check if we're pasting into a heading
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let container = range.commonAncestorContainer;
      
      // Walk up to find if we're in a heading
      while (container && container !== editorRef.current) {
        if (container.nodeName === 'H1' || container.nodeName === 'H2') {
          // We're in a heading - paste as plain text to maintain heading style
          document.execCommand('insertText', false, text);
          handleInput();
          return;
        }
        container = container.parentNode;
      }
    }
    
    // Normal paste - preserve formatting
    if (html) {
      document.execCommand('insertHTML', false, html);
    } else {
      document.execCommand('insertText', false, text);
    }
    
    setTimeout(() => handleInput(), 0);
  };

  return (
    <div className={cn("rounded-tile bg-mist overflow-hidden", className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-line flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => applyFormat('bold')}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => applyFormat('italic')}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={clearFormatting}
          title="Clear formatting"
        >
          <Eraser className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-line mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => applyHeading('h1')}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => applyHeading('h2')}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={applyParagraph}
          title="Paragraph"
        >
          <Pilcrow className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-line mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => applyFormat('insertUnorderedList')}
          title="Bullet list"
        >
          <List className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-line mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => insertColumns(2)}
          title="Two columns"
        >
          <Columns2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 rounded-pill text-muted-foreground hover:text-foreground hover:bg-surface"
          onClick={() => insertColumns(3)}
          title="Three columns"
        >
          <Columns3 className="h-4 w-4" />
        </Button>
        <div className="flex-1" />
        {linkText && linkHref ? (
          <a 
            href={linkHref} 
            className="text-xs text-clay hover:underline hidden sm:block"
          >
            {linkText}
          </a>
        ) : (
          <span className="text-xs text-muted-foreground hidden sm:block">Format text</span>
        )}
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPaste={handlePaste}
        className={cn(
          "p-3 outline-none min-h-[120px]",
          !editorRef.current?.innerHTML && !isFocused && "text-muted-foreground"
        )}
        style={{ 
          minHeight: `${rows * 1.5}em`,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          lineHeight: '1.5'
        }}
        data-placeholder={placeholder}
      />
      
      <style>{`
        [contentEditable]:empty:before {
          content: attr(data-placeholder);
          color: hsl(var(--ink-muted));
        }
        [contentEditable] h1 {
          font-size: 1.4em;
          font-weight: 700;
          margin: 0.5em 0;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        [contentEditable] h2 {
          font-size: 1.15em;
          font-weight: 500;
          margin: 0.4em 0;
        }
        [contentEditable] ul {
          margin: 0.5em 0;
          padding-left: 0;
          list-style-type: disc;
          list-style-position: inside;
        }
        [contentEditable] li {
          display: list-item;
          list-style-position: inside;
          list-style-type: disc;
        }
        [contentEditable] p {
          margin: 0.3em 0;
        }
        [contentEditable] table {
          width: 100%;
          margin: 12px 0;
          border-collapse: collapse;
        }
        [contentEditable] table.column-table {
          width: 100%;
          margin: 12px 0;
          border-collapse: collapse;
        }
        [contentEditable] table.column-table td {
          vertical-align: top !important;
          padding: 0 !important;
          padding-right: 15px !important;
          border: none !important;
          background: none !important;
        }
        [contentEditable] table.column-table td:last-child {
          padding-right: 0 !important;
        }
        [contentEditable] table.column-table h1 {
          font-size: 1.25em;
          font-weight: 700;
          margin: 0.3em 0;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        [contentEditable] table.column-table h2 {
          font-size: 1.1em;
          font-weight: 500;
          margin: 0.3em 0;
        }
        [contentEditable] table.column-table p {
          font-size: 1em;
          margin: 0.2em 0;
        }
        [contentEditable] table.column-table ul {
          padding-left: 0;
          margin: 0.3em 0;
        }
        [contentEditable] table.column-table li {
          list-style-position: inside;
        }
        [contentEditable] table.column-table li::marker {
          margin-right: 2px;
        }
        [contentEditable] table.column-table strong {
          font-weight: 600;
        }
        [contentEditable] table.column-table em {
          font-style: italic;
        }
      `}</style>
    </div>
  );
}
